import type {AssetDownloadLink, AssetDto} from '@hc/base'
import {useAppStore} from '../../app'

export interface AssetUrlOptions {
  display?: boolean
}

export interface AssetOpenOptions extends AssetUrlOptions {
  newTab?: boolean
}

type AssetRef = AssetDto | string

const assetId = (assetOrId: AssetRef): string => typeof assetOrId === 'string' ? assetOrId : assetOrId.id

/**
 * Opens / downloads assets without navigating the app origin:
 * the presigned storage url is requested with the Authorization header (GET <core>/storage/assets/:id/link)
 * and the browser then goes to the storage provider directly. This avoids service worker navigation fallbacks
 * (which would answer same origin navigations with the app shell) and keeps the jwt out of urls.
 */
export function useAssetUrl() {
  const app = useAppStore()

  function endpoint(assetOrId: AssetRef, action: 'link' | 'download', opts?: AssetUrlOptions): string {
    const query = opts?.display ? '?display=1' : ''
    return app.config.serviceUrl('core', `/storage/assets/${encodeURIComponent(assetId(assetOrId))}/${action}`) + query
  }

  async function authorizedFetch(url: string): Promise<Response> {
    const response = await fetch(url, {headers: {Authorization: 'Bearer ' + app.auth.jwt}})
    if (!response.ok) throw new Error(response.status === 404 ? 'File not found' : `Download failed (HTTP ${response.status})`)
    return response
  }

  // short lived presigned url (null if the storage can not presign)
  async function resolveLink(assetOrId: AssetRef, opts?: AssetUrlOptions): Promise<AssetDownloadLink> {
    const response = await authorizedFetch(endpoint(assetOrId, 'link', opts))
    return await response.json() as AssetDownloadLink
  }

  // fallback when the storage can not presign: fetch the bytes with the Authorization header
  async function fetchBlobUrl(assetOrId: AssetRef, opts?: AssetUrlOptions): Promise<string> {
    const response = await authorizedFetch(endpoint(assetOrId, 'download', opts))
    return URL.createObjectURL(await response.blob())
  }

  function clickLink(url: string, filename?: string): void {
    const link = document.createElement('a')
    link.href = url
    link.rel = 'noopener'
    if (filename) link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  async function open(assetOrId: AssetRef, opts?: AssetOpenOptions): Promise<void> {
    const inNewTab = !!(opts?.display || opts?.newTab)
    // open the tab synchronously within the click, otherwise popup blockers interfere after the await
    const tab = inNewTab ? window.open('', '_blank') : null
    if (tab) tab.opener = null
    try {
      const link = await resolveLink(assetOrId, opts)
      if (link.url) {
        if (tab) tab.location.href = link.url
        else if (inNewTab) window.open(link.url, '_blank', 'noopener')
        else clickLink(link.url)
        return
      }
      const blobUrl = await fetchBlobUrl(assetOrId, opts)
      if (tab) tab.location.href = blobUrl
      else clickLink(blobUrl, link.filename)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
    } catch (e) {
      if (tab) tab.close()
      throw e
    }
  }

  return {resolveLink, open}
}
